import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { onlineOrderingLinks } from "@/components/lib/SidebarLinks";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "react-hot-toast";
import { cn } from "@/lib/utils";
import { restaurant_id } from "@/api/lib/url";
import useAuth from "@/hooks/useAuth";
import {
  ShoppingBag,
  Bike,
  Info,
  Save,
  RotateCcw,
  CheckCircle2,
} from "lucide-react";
import {
  fetchServiceChargeSettings,
  saveServiceChargeSettings,
  clearServiceChargeMessages,
} from "@/redux/slice/order-ordering-slice/service-charge-slice";

function ServiceChargeSkeleton() {
  return (
    <div className="container mt-4">
      {/* Header skeleton */}
      <div className="flex flex-col sm:flex-row justify-between gap-4 pb-4 border-b border-cblack6">
        <div>
          <Skeleton className="h-6 w-64 bg-cblack7 rounded-md" />
          <Skeleton className="h-4 w-96 bg-cblack7/70 rounded-md mt-2" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-7 w-28 bg-cblack7 rounded-full" />
          <Skeleton className="h-7 w-28 bg-cblack7 rounded-full" />
        </div>
      </div>

      {/* Cards skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {[1, 2].map((i) => (
          <Card key={i} className="bg-cblack3 border border-cblack6 rounded-xl overflow-hidden">
            <CardContent className="p-6 space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Skeleton className="w-11 h-11 rounded-xl bg-cblack7" />
                  <div className="space-y-1.5">
                    <Skeleton className="h-5 w-32 bg-cblack7" />
                    <Skeleton className="h-3.5 w-44 bg-cblack7/70" />
                  </div>
                </div>
                <Skeleton className="h-6 w-20 rounded-full bg-cblack7" />
              </div>

              <div className="space-y-4 pt-2">
                <Skeleton className="h-16 w-full rounded-xl bg-cblack7/50" />
                <Skeleton className="h-16 w-full rounded-xl bg-cblack7/50" />
                <Skeleton className="h-20 w-full rounded-xl bg-cblack7/50" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Footer skeleton */}
      <div className="mt-6 p-4 rounded-xl bg-cblack3 border border-cblack6 flex justify-between items-center">
        <Skeleton className="h-5 w-48 bg-cblack7" />
        <div className="flex gap-3">
          <Skeleton className="h-10 w-24 bg-cblack7 rounded-lg" />
          <Skeleton className="h-10 w-32 bg-cblack7 rounded-lg" />
        </div>
      </div>
    </div>
  );
}

const OnlineOrderSettings = () => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const currentRestaurantId =
    user?.restaurant_id || localStorage.getItem("restaurant_id") || restaurant_id;

  const {
    serviceCharge,
    getLoading,
    saveLoading,
    error,
    success,
  } = useSelector((state) => state.onlineOrdering.serviceCharge);

  // Local editable form state covering all 6 fields from the API
  const [formState, setFormState] = useState({
    pickup_enabled: true,
    delivery_enabled: true,
    pickup_service_charge_enabled: true,
    delivery_service_charge_enabled: true,
    pickup_service_charge: "0.00",
    delivery_service_charge: "0.00",
  });

  // Fetch settings on mount
  useEffect(() => {
    if (currentRestaurantId) {
      dispatch(fetchServiceChargeSettings(currentRestaurantId));
    }
    return () => {
      dispatch(clearServiceChargeMessages());
    };
  }, [dispatch, currentRestaurantId]);

  // Sync fetched Redux data to local form state
  useEffect(() => {
    if (serviceCharge) {
      setFormState({
        pickup_enabled: serviceCharge.pickup_enabled ?? true,
        delivery_enabled: serviceCharge.delivery_enabled ?? true,
        pickup_service_charge_enabled: serviceCharge.pickup_service_charge_enabled ?? true,
        delivery_service_charge_enabled: serviceCharge.delivery_service_charge_enabled ?? true,
        pickup_service_charge:
          serviceCharge.pickup_service_charge != null
            ? String(serviceCharge.pickup_service_charge)
            : "0.00",
        delivery_service_charge:
          serviceCharge.delivery_service_charge != null
            ? String(serviceCharge.delivery_service_charge)
            : "0.00",
      });
    }
  }, [serviceCharge]);

  // Toast feedback
  useEffect(() => {
    if (success) {
      toast.success(success);
      dispatch(clearServiceChargeMessages());
    }
    if (error) {
      toast.error(error);
      dispatch(clearServiceChargeMessages());
    }
  }, [success, error, dispatch]);

  // Compute whether form is modified compared to Redux state
  const isDirty = useMemo(() => {
    if (!serviceCharge) return false;

    const serverPickupEnabled = serviceCharge.pickup_enabled ?? true;
    const serverDeliveryEnabled = serviceCharge.delivery_enabled ?? true;
    const serverPickupScEnabled = serviceCharge.pickup_service_charge_enabled ?? true;
    const serverDeliveryScEnabled = serviceCharge.delivery_service_charge_enabled ?? true;
    const serverPickupSc = parseFloat(serviceCharge.pickup_service_charge) || 0;
    const serverDeliverySc = parseFloat(serviceCharge.delivery_service_charge) || 0;

    const currentPickupSc = parseFloat(formState.pickup_service_charge) || 0;
    const currentDeliverySc = parseFloat(formState.delivery_service_charge) || 0;

    return (
      Boolean(formState.pickup_enabled) !== Boolean(serverPickupEnabled) ||
      Boolean(formState.delivery_enabled) !== Boolean(serverDeliveryEnabled) ||
      Boolean(formState.pickup_service_charge_enabled) !== Boolean(serverPickupScEnabled) ||
      Boolean(formState.delivery_service_charge_enabled) !== Boolean(serverDeliveryScEnabled) ||
      currentPickupSc !== serverPickupSc ||
      currentDeliverySc !== serverDeliverySc
    );
  }, [formState, serviceCharge]);

  // Reset form back to Redux state
  const handleReset = useCallback(() => {
    if (serviceCharge) {
      setFormState({
        pickup_enabled: serviceCharge.pickup_enabled ?? true,
        delivery_enabled: serviceCharge.delivery_enabled ?? true,
        pickup_service_charge_enabled: serviceCharge.pickup_service_charge_enabled ?? true,
        delivery_service_charge_enabled: serviceCharge.delivery_service_charge_enabled ?? true,
        pickup_service_charge:
          serviceCharge.pickup_service_charge != null
            ? String(serviceCharge.pickup_service_charge)
            : "0.00",
        delivery_service_charge:
          serviceCharge.delivery_service_charge != null
            ? String(serviceCharge.delivery_service_charge)
            : "0.00",
      });
      toast("Changes reset to saved settings", { icon: "↩️" });
    }
  }, [serviceCharge]);

  // Save all settings via PUT endpoint
  const handleSave = useCallback(() => {
    const payload = {
      pickup_enabled: Boolean(formState.pickup_enabled),
      delivery_enabled: Boolean(formState.delivery_enabled),
      pickup_service_charge_enabled: Boolean(formState.pickup_service_charge_enabled),
      delivery_service_charge_enabled: Boolean(formState.delivery_service_charge_enabled),
      pickup_service_charge: parseFloat(formState.pickup_service_charge) || 0,
      delivery_service_charge: parseFloat(formState.delivery_service_charge) || 0,
    };

    dispatch(
      saveServiceChargeSettings({
        restaurantId: currentRestaurantId,
        data: payload,
      })
    );
  }, [formState, currentRestaurantId, dispatch]);

  const handleNumberChange = (field, val) => {
    // Only allow positive numbers/decimals
    if (val === "" || /^\d*\.?\d*$/.test(val)) {
      setFormState((prev) => ({ ...prev, [field]: val }));
    }
  };

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={onlineOrderingLinks} />}
      pageName={{ title: "Order Settings" }}
      paths={[
        // { name: "Home", path: "/home" },
        // { name: "Online Ordering", path: "/online-ordering" },
        {
          name: "Settings",
          path: "/onlineordering/settings/website-settings",
        },
        { name: "Order Settings" },
      ]}
    >
      <div className="container mt-4 pb-12">
        {getLoading ? (
          <ServiceChargeSkeleton />
        ) : (
          <div>
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-cblack6">
              <div>
                <h2 className="text-xl font-semibold text-cwhite tracking-tight">
                  Order Channels & Service Charges
                </h2>
                <p className="text-sm text-color4 mt-1">
                  Manage pickup and delivery channel availability and configure customer service fee rates.
                </p>
              </div>

              {/* Status Pills */}
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={cn(
                    "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                    formState.pickup_enabled
                      ? "bg-cgreen/10 text-cgreen border-cgreen/30"
                      : "bg-cblack7 text-color4 border-cblack6"
                  )}
                >
                  <span
                    className={cn(
                      "w-2 h-2 rounded-full",
                      formState.pickup_enabled ? "bg-cgreen animate-pulse" : "bg-color4"
                    )}
                  />
                  Pickup: {formState.pickup_enabled ? "Active" : "Disabled"}
                </span>

                <span
                  className={cn(
                    "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                    formState.delivery_enabled
                      ? "bg-cgreen/10 text-cgreen border-cgreen/30"
                      : "bg-cblack7 text-color4 border-cblack6"
                  )}
                >
                  <span
                    className={cn(
                      "w-2 h-2 rounded-full",
                      formState.delivery_enabled ? "bg-cgreen animate-pulse" : "bg-color4"
                    )}
                  />
                  Delivery: {formState.delivery_enabled ? "Active" : "Disabled"}
                </span>
              </div>
            </div>

            {/* Dual Cards Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              {/* Pickup Channel Card */}
              <Card className="bg-cblack3 border border-cblack6 hover:border-cblack7 transition-all duration-300 rounded-xl shadow-lg overflow-hidden flex flex-col justify-between">
                <div>
                  {/* Card Accent Top Banner */}
                  <div className="h-1 bg-gradient-to-r from-cyellow/80 via-cyellow to-amber-500" />
                  
                  <CardContent className="p-6">
                    {/* Header */}
                    <div className="flex items-center justify-between pb-5 border-b border-cblack6">
                      <div className="flex items-center gap-3.5">
                        <div className="w-11 h-11 rounded-xl bg-cyellow/10 border border-cyellow/20 flex items-center justify-center text-cyellow shadow-inner">
                          <ShoppingBag className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="text-base font-semibold text-cwhite">
                            Pickup Orders
                          </h3>
                          <p className="text-xs text-color4">
                            Click & Collect / Customer Collection
                          </p>
                        </div>
                      </div>

                      <Badge
                        className={cn(
                          "text-xs px-2.5 py-0.5 font-medium transition-colors",
                          formState.pickup_enabled
                            ? "bg-cgreen/15 text-cgreen border border-cgreen/30"
                            : "bg-cblack7 text-color4 border border-cblack6"
                        )}
                      >
                        {formState.pickup_enabled ? "Open for Orders" : "Channel Closed"}
                      </Badge>
                    </div>

                    {/* Controls */}
                    <div className="space-y-4 pt-5">
                      {/* Channel Availability Toggle */}
                      <div className="flex items-center justify-between p-4 rounded-xl bg-cblack2/90 border border-cblack6/70 hover:border-cblack6 transition-colors">
                        <div className="pr-3">
                          <p className="text-sm font-medium text-cwhite">
                            Accept Pickup Orders
                          </p>
                          <p className="text-xs text-color4 mt-0.5">
                            Allow customers to select pickup option in website or app.
                          </p>
                        </div>
                        <Switch
                          id="pickup-toggle"
                          checked={formState.pickup_enabled}
                          onCheckedChange={(checked) =>
                            setFormState((prev) => ({ ...prev, pickup_enabled: checked }))
                          }
                        />
                      </div>

                      {/* Service Charge Toggle */}
                      <div className="flex items-center justify-between p-4 rounded-xl bg-cblack2/90 border border-cblack6/70 hover:border-cblack6 transition-colors">
                        <div className="pr-3">
                          <p className="text-sm font-medium text-cwhite">
                            Apply Service Charge
                          </p>
                          <p className="text-xs text-color4 mt-0.5">
                            Add a service charge fee to each online pickup order.
                          </p>
                        </div>
                        <Switch
                          id="pickup-sc-toggle"
                          checked={formState.pickup_service_charge_enabled}
                          onCheckedChange={(checked) =>
                            setFormState((prev) => ({
                              ...prev,
                              pickup_service_charge_enabled: checked,
                            }))
                          }
                        />
                      </div>

                      {/* Service Charge Input Field */}
                      <div
                        className={cn(
                          "p-4 rounded-xl bg-cblack2/90 border border-cblack6/70 transition-all",
                          !formState.pickup_service_charge_enabled && "opacity-50"
                        )}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <label
                            htmlFor="pickup-sc-input"
                            className="text-sm font-medium text-cwhite flex items-center gap-1.5"
                          >
                            <span>Pickup Service Charge Fee</span>
                          </label>
                          <span className="text-xs text-color4">GBP (£)</span>
                        </div>

                        <div className="relative">
                          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-color4 font-semibold text-base select-none">
                            £
                          </span>
                          <Input
                            id="pickup-sc-input"
                            type="text"
                            inputMode="decimal"
                            disabled={!formState.pickup_service_charge_enabled}
                            value={formState.pickup_service_charge}
                            onChange={(e) =>
                              handleNumberChange("pickup_service_charge", e.target.value)
                            }
                            placeholder="0.00"
                            className={cn(
                              "pl-8 bg-cblack border-cblack6 text-cwhite font-medium focus:border-cyellow focus:ring-1 focus:ring-cyellow h-11 text-base rounded-lg transition-all",
                              !formState.pickup_service_charge_enabled &&
                                "cursor-not-allowed bg-cblack4 text-gray-500"
                            )}
                          />
                        </div>

                        <p className="text-xs text-color4 mt-2 flex items-center gap-1.5">
                          <Info className="w-3.5 h-3.5 text-color4 shrink-0" />
                          {formState.pickup_service_charge_enabled
                            ? "This fixed fee will be added to the customer's checkout summary."
                            : "Enable service charge above to apply this fee on pickup orders."}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </div>
              </Card>

              {/* Delivery Channel Card */}
              <Card className="bg-cblack3 border border-cblack6 hover:border-cblack7 transition-all duration-300 rounded-xl shadow-lg overflow-hidden flex flex-col justify-between">
                <div>
                  {/* Card Accent Top Banner */}
                  <div className="h-1 bg-gradient-to-r from-cblue/80 via-cblue to-sky-400" />

                  <CardContent className="p-6">
                    {/* Header */}
                    <div className="flex items-center justify-between pb-5 border-b border-cblack6">
                      <div className="flex items-center gap-3.5">
                        <div className="w-11 h-11 rounded-xl bg-cblue/10 border border-cblue/20 flex items-center justify-center text-cblue shadow-inner">
                          <Bike className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="text-base font-semibold text-cwhite">
                            Delivery Orders
                          </h3>
                          <p className="text-xs text-color4">
                            Home & Office Doorstep Delivery
                          </p>
                        </div>
                      </div>

                      <Badge
                        className={cn(
                          "text-xs px-2.5 py-0.5 font-medium transition-colors",
                          formState.delivery_enabled
                            ? "bg-cgreen/15 text-cgreen border border-cgreen/30"
                            : "bg-cblack7 text-color4 border border-cblack6"
                        )}
                      >
                        {formState.delivery_enabled ? "Open for Orders" : "Channel Closed"}
                      </Badge>
                    </div>

                    {/* Controls */}
                    <div className="space-y-4 pt-5">
                      {/* Channel Availability Toggle */}
                      <div className="flex items-center justify-between p-4 rounded-xl bg-cblack2/90 border border-cblack6/70 hover:border-cblack6 transition-colors">
                        <div className="pr-3">
                          <p className="text-sm font-medium text-cwhite">
                            Accept Delivery Orders
                          </p>
                          <p className="text-xs text-color4 mt-0.5">
                            Allow customers to select delivery option in website or app.
                          </p>
                        </div>
                        <Switch
                          id="delivery-toggle"
                          checked={formState.delivery_enabled}
                          onCheckedChange={(checked) =>
                            setFormState((prev) => ({ ...prev, delivery_enabled: checked }))
                          }
                        />
                      </div>

                      {/* Service Charge Toggle */}
                      <div className="flex items-center justify-between p-4 rounded-xl bg-cblack2/90 border border-cblack6/70 hover:border-cblack6 transition-colors">
                        <div className="pr-3">
                          <p className="text-sm font-medium text-cwhite">
                            Apply Service Charge
                          </p>
                          <p className="text-xs text-color4 mt-0.5">
                            Add a service charge fee to each online delivery order.
                          </p>
                        </div>
                        <Switch
                          id="delivery-sc-toggle"
                          checked={formState.delivery_service_charge_enabled}
                          onCheckedChange={(checked) =>
                            setFormState((prev) => ({
                              ...prev,
                              delivery_service_charge_enabled: checked,
                            }))
                          }
                        />
                      </div>

                      {/* Service Charge Input Field */}
                      <div
                        className={cn(
                          "p-4 rounded-xl bg-cblack2/90 border border-cblack6/70 transition-all",
                          !formState.delivery_service_charge_enabled && "opacity-50"
                        )}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <label
                            htmlFor="delivery-sc-input"
                            className="text-sm font-medium text-cwhite flex items-center gap-1.5"
                          >
                            <span>Delivery Service Charge Fee</span>
                          </label>
                          <span className="text-xs text-color4">GBP (£)</span>
                        </div>

                        <div className="relative">
                          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-color4 font-semibold text-base select-none">
                            £
                          </span>
                          <Input
                            id="delivery-sc-input"
                            type="text"
                            inputMode="decimal"
                            disabled={!formState.delivery_service_charge_enabled}
                            value={formState.delivery_service_charge}
                            onChange={(e) =>
                              handleNumberChange("delivery_service_charge", e.target.value)
                            }
                            placeholder="0.00"
                            className={cn(
                              "pl-8 bg-cblack border-cblack6 text-cwhite font-medium focus:border-cblue focus:ring-1 focus:ring-cblue h-11 text-base rounded-lg transition-all",
                              !formState.delivery_service_charge_enabled &&
                                "cursor-not-allowed bg-cblack4 text-gray-500"
                            )}
                          />
                        </div>

                        <p className="text-xs text-color4 mt-2 flex items-center gap-1.5">
                          <Info className="w-3.5 h-3.5 text-color4 shrink-0" />
                          {formState.delivery_service_charge_enabled
                            ? "This fixed fee will be added to the customer's checkout summary."
                            : "Enable service charge above to apply this fee on delivery orders."}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </div>
              </Card>
            </div>

            {/* Bottom Sticky/Docked Action Bar */}
            <div className="mt-8 p-4 rounded-xl bg-cblack3 border border-cblack6 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-xl">
              <div className="flex items-center gap-2.5 text-sm">
                {isDirty ? (
                  <div className="flex items-center gap-2 text-cyellow">
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyellow opacity-75" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-cyellow" />
                    </span>
                    <span className="font-medium">You have unsaved changes</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-color4">
                    <CheckCircle2 className="w-4 h-4 text-cgreen shrink-0" />
                    <span>All settings are saved and up to date</span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleReset}
                  disabled={!isDirty || saveLoading}
                  className="border-cblack6 bg-cblack7/40 text-cwhite hover:bg-cblack7 hover:text-white px-4 h-10 transition-colors"
                >
                  <RotateCcw className="w-4 h-4 mr-1.5" />
                  Reset
                </Button>

                <Button
                  type="button"
                  onClick={handleSave}
                  isLoading={saveLoading}
                  disabled={saveLoading || !isDirty}
                  className="bg-cgreen hover:bg-cgreen/90 text-white font-medium px-6 h-10 shadow-lg shadow-cgreen/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-4 h-4 mr-1.5" />
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout2>
  );
};

export default OnlineOrderSettings;

